import { useRef, useEffect, type RefObject } from 'react'

/**
 * WAI-ARIA dialog pattern for any modal-like surface.
 * Handles focus trap, scroll lock, Escape, and arrow-key list navigation.
 * Ported from v56.2 lines 460-563.
 *
 * Caller is still responsible for role="dialog", aria-modal, aria-labelledby,
 * and rendering a close button.
 */
export function useDialogA11y(
  ref: RefObject<HTMLElement | null>,
  show: boolean,
  onClose: () => void,
  closable = true,
): void {
  // Latest-ref pattern to avoid tearing down the focus trap on every
  // parent re-render that creates a new onClose function reference.
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose })

  const previousActiveRef = useRef<Element | null>(null)

  useEffect(() => {
    if (!show) return

    // Snapshot focused element to restore on close.
    previousActiveRef.current = document.activeElement

    // Focus dialog container (not first tabbable) so screen readers
    // announce the dialog title via aria-labelledby.
    const container = ref.current
    if (container) container.focus({ preventScroll: true })

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closable) {
        e.preventDefault()
        onCloseRef.current()
        return
      }

      if (e.key === 'Tab' && ref.current) {
        const focusable = Array.from(
          ref.current.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter(el => el.offsetParent !== null)

        if (focusable.length === 0) { e.preventDefault(); return }
        const first = focusable[0]
        const last = focusable[focusable.length - 1]

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus()
        }
      }

      // Arrow-key navigation between list rows (chain/token/fiat pickers).
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Home' || e.key === 'End') {
        if (!ref.current) return
        const ae = document.activeElement as HTMLElement | null
        if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA')) return

        const rows = Array.from(
          ref.current.querySelectorAll<HTMLButtonElement>('button:not([disabled])'),
        ).filter(el => el.offsetParent !== null)

        if (rows.length === 0) return
        const currentIdx = rows.indexOf(ae as HTMLButtonElement)
        let nextIdx: number
        if (e.key === 'Home') nextIdx = 0
        else if (e.key === 'End') nextIdx = rows.length - 1
        else if (e.key === 'ArrowDown') nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % rows.length
        else /* ArrowUp */ nextIdx = currentIdx <= 0 ? rows.length - 1 : currentIdx - 1

        e.preventDefault()
        rows[nextIdx].focus()
      }
    }
    document.addEventListener('keydown', handleKey)

    // Lock background scroll (prevents scroll-through on iOS).
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = prevOverflow

      // Restore focus to triggering element if it's still in the DOM.
      const prev = previousActiveRef.current as HTMLElement | null
      if (prev && typeof prev.focus === 'function' && document.contains(prev)) {
        prev.focus({ preventScroll: true })
      }
    }
  }, [show, closable, ref])
}

/**
 * Stable ID generator for dialog title → aria-labelledby linkage.
 * Not using React.useId because it's React 18+ only.
 */
export function useStableId(prefix = 'id'): string {
  const [id] = [useRef(`${prefix}-${Math.random().toString(36).slice(2, 9)}`)]
  return id.current
}
